import React, { ReactNode, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import Button, { ButtonTheme } from 'components/Form/Button'
import Card from 'components/Layout/Card'
import FormColumn from 'components/Form/FormColumn'
import FormRow from 'components/Form/FormRow'
import Input from 'components/Form/Input'
import SelectAsync, { SelectAsyncValue } from 'components/Form/SelectAsync'
import { AppContext } from 'contexts/AppContext'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { Agency } from 'types/agency'
import { FormErrors, UserEditForm } from './types'
import { ReactRouterParams } from 'types/various'
import { BaseRole, UserStatus } from 'types/user'
import UserService, { BaseRoleSelectRawData } from '../service'
import UserEditService from './user-edit.service'
import '../User.scss'
import { PRIORITIES } from '../../../constant'
import RangeSlider from 'components/Form/RangeSlider'
import UserChangeConfirmationModal from '../UserChangeConfirmationModal'
import ResetPasswordModal from '../ResetPasswordModal'
import ReInviteModal from '../ReinviteModal'
import ChangeEmailModal from '../ChangeEmailModal'
import UserEditFormService from './user-edit-form.service'
import Checkbox from '../../../components/Form/Checkbox'

const UserEdit: React.FC = () => {
  const { addNotification, updateUser } = useContext(AppContext)
  const { t } = useTranslation()
  const { id: urlId } = useParams<ReactRouterParams>()
  const [userForm, setUserForm] = useState<UserEditForm>()
  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<FormErrors>()
  const [priorities, setPriorities] = useState<number[]>([])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false)
  const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false)
  const [resetUrl, setResetUrl] = useState<string | undefined>(undefined)
  const [reInviteModalOpen, setReInviteModalOpen] = useState<boolean>(false)
  const [invitationUrl, setInvitationUrl] = useState<string | undefined>(undefined)
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState<boolean>(false)
  const [defaultSubscription, setDefaultSubscription] = useState<boolean>(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (urlId) UserEditService.getUser(urlId, setUserForm, setLoading, setDefaultSubscription)
  }, [urlId])

  useEffect(() => {
    if (!UserEditFormService.hasErrors(errors) && userForm) {
      UserEditService.updateUser(userForm, setLoading, addNotification, navigate, updateUser, t)
    }
  }, [errors])

  useEffect(() => {
    if (!userForm || priorities.length) {
      return
    }

    setPriorities(userForm.priorities)
  }, [priorities, userForm])

  const hasPrioritiesBeenChanged = (): boolean => {
    if (!userForm || !userForm.priorities) {
      return false
    }

    return (
      userForm.priorities[0] !== priorities[0] ||
      userForm.priorities[userForm.priorities.length - 1] !== priorities[priorities.length - 1]
    )
  }

  const onSave = (userForm: UserEditForm) =>
    void UserEditFormService.validate(userForm, errors, setErrors)
  const Form = (userForm: UserEditForm): ReactNode => (
    <>
      <div className='User__form'>
        <Card title={t('user.edit.header')}>
          <FormRow>
            <FormColumn>
              <Checkbox
                id='subscriptionAccount'
                checked={userForm.subscriptionAccount}
                onChange={(event): void => {
                  setUserForm({
                    ...userForm,
                    subscriptionAccount: event.target.checked,
                    roles: [],
                    agencies: [],
                  })
                }}
              >
                {t('user.common.subscriptionAccount')}
              </Checkbox>
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Input
                id='firstName'
                title={t('common.firstName')}
                placeholder={t('common.enterFirstName')}
                value={userForm.firstName}
                onChange={firstName => void setUserForm({ ...userForm, firstName })}
                disabled={loading}
                errors={errors?.firstName}
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Input
                id='lastName'
                title={t('common.lastName')}
                placeholder={t('common.enterLastName')}
                value={userForm.lastName}
                onChange={lastName => void setUserForm({ ...userForm, lastName })}
                disabled={loading}
                errors={errors?.lastName}
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <RangeSlider
              value={[userForm.priorities[0], userForm.priorities[userForm.priorities.length - 1]]}
              min={PRIORITIES[0]}
              max={PRIORITIES[PRIORITIES.length - 1]}
              title={t('common.priority')}
              help={t('form.campaign.details.priorityHelp')}
              onChange={priorities => {
                void setUserForm({
                  ...userForm,
                  priorities: UserService.convertScopeToRange(priorities as [number, number]),
                })
              }}
              id={'RangeSlider'}
              minLabel={t('priority.low')}
              maxLabel={t('priority.high')}
            ></RangeSlider>
          </FormRow>

          <FormRow>
            {userForm.subscriptionAccount && (
              <SelectAsync
                id='roles'
                title={t('common.roles')}
                placeholder={t('common.rolesPlaceholder')}
                value={userForm.roles}
                onChange={roles => {
                  userForm.agencies = []
                  setUserForm({
                    ...userForm,
                    roles,
                    agencies: userForm.agencies,
                  })
                }}
                options={(): Promise<SelectAsyncValue<BaseRoleSelectRawData>[]> =>
                  UserService.getRoles(true)
                }
                multi
                closeOnSelect={false}
                disabled={loading}
                errors={errors?.roles}
              />
            )}

            {!userForm.subscriptionAccount && (
              <SelectAsync
                id='roles'
                title={t('common.roles')}
                placeholder={t('common.rolesPlaceholder')}
                value={userForm.roles}
                onChange={roles => {
                  userForm.agencies = []
                  setUserForm({
                    ...userForm,
                    roles,
                    agencies: userForm.agencies,
                  })
                }}
                options={(): Promise<SelectAsyncValue<BaseRoleSelectRawData>[]> =>
                  UserService.getRoles(false)
                }
                multi
                closeOnSelect={false}
                disabled={loading}
                errors={errors?.roles}
              />
            )}
          </FormRow>

          <FormRow>
            <SelectAsync
              id='agencies'
              title={t('common.agency')}
              value={userForm.agencies}
              multi={userForm.roles.some(
                role =>
                  role.rawData?.baseRole === BaseRole.ADMIN ||
                  role.rawData?.baseRole === BaseRole.EMPLOYEE
              )}
              options={(inputValue: string): Promise<SelectAsyncValue<Agency>[]> =>
                UserService.getAgencies(inputValue)
              }
              onChange={(agencies): void =>
                void setUserForm({
                  ...userForm,
                  agencies: Array.isArray(agencies) ? agencies : [agencies],
                })
              }
              disabled={UserService.shouldDisableClientInput(userForm.roles) || loading}
              errors={errors?.agencies}
            />
          </FormRow>

          <FormRow>
            <FormColumn>
              <Input
                id='email'
                title={t('common.email')}
                placeholder={t('common.enterEmail')}
                value={userForm.email}
                onChange={() => void {}}
                disabled
              />
            </FormColumn>
          </FormRow>

          {userForm.status === UserStatus.INVITED ||
            (defaultSubscription && !userForm.subscriptionAccount && (
              <div className='User__actions User__actions--full'>
                <Button
                  icon={loading ? faSpinner : undefined}
                  disabled={loading}
                  onClick={async () => {
                    setLoading(true)
                    const url = await UserEditService.reInviteUser(userForm.id)
                    setInvitationUrl(url)
                    setLoading(false)
                    setReInviteModalOpen(true)
                  }}
                >
                  {t('user.reInviteModal.action')}
                </Button>
              </div>
            ))}

          {userForm.status === UserStatus.ACTIVE && (
            <div className='User__actions User__actions--full'>
              <Button
                icon={loading ? faSpinner : undefined}
                disabled={loading}
                onClick={async () => {
                  setChangeEmailModalOpen(true)
                }}
              >
                {t('user.changeEmailModal.action')}
              </Button>
            </div>
          )}

          <div className='User__actions'>
            <Button
              icon={loading ? faSpinner : undefined}
              disabled={loading}
              dataCy={'user-edit-save-button'}
              onClick={() => {
                if (!hasPrioritiesBeenChanged()) {
                  return onSave(userForm)
                }

                setIsConfirmModalOpen(true)
              }}
            >
              {t('common.save')}
            </Button>

            {!userForm.subscriptionAccount && userForm.status === UserStatus.ACTIVE && (
              <Button
                theme={ButtonTheme.BLUE_OUTLINE}
                icon={loading ? faSpinner : undefined}
                disabled={loading}
                onClick={async () => {
                  setLoading(true)
                  const url = await UserEditService.initiatePasswordReset(userForm.id)
                  setResetUrl(url)
                  setLoading(false)
                  setIsResetModalOpen(true)
                }}
              >
                {t('common.resetPassword')}
              </Button>
            )}
          </div>
        </Card>
      </div>

      {isConfirmModalOpen && (
        <UserChangeConfirmationModal
          isOpen={isConfirmModalOpen}
          setIsOpen={setIsConfirmModalOpen}
          save={onSave}
          user={userForm}
        />
      )}

      {isResetModalOpen && (
        <ResetPasswordModal
          isOpen={isResetModalOpen}
          setIsOpen={setIsResetModalOpen}
          resetUrl={resetUrl}
        />
      )}

      {reInviteModalOpen && (
        <ReInviteModal
          isOpen={reInviteModalOpen}
          setIsOpen={setReInviteModalOpen}
          invitationUrl={invitationUrl}
        />
      )}

      {changeEmailModalOpen && (
        <ChangeEmailModal
          isOpen={changeEmailModalOpen}
          setIsOpen={setChangeEmailModalOpen}
          currentEmail={userForm.email}
          userId={userForm.id}
          setUser={setUserForm}
          setLoading={setLoading}
        />
      )}
    </>
  )

  return <>{userForm ? Form(userForm) : t('common.noDataFound')}</>
}

export default UserEdit
